import { useCallback } from 'react';
import { ApiError, useApiConfig } from '@/providers/ApiConfigProvider';

export { ApiError };

/**
 * Reads the body of a successful response. Endpoints that only acknowledge work answer without one
 * (202 Accepted, 204 No Content), so parsing unconditionally would fail a request that succeeded.
 */
async function readBody<T>(response: Response): Promise<T> {
  const body = await response.text();

  return body === '' ? (undefined as T) : (JSON.parse(body) as T);
}

/**
 * Hook that provides authenticated API methods.
 * Uses the ApiConfigProvider for base URL and authentication headers.
 */
export function useApi() {
  const { apiBaseUrl, getHeaders, authenticatedFetch } = useApiConfig();

  const apiGet = useCallback(
    async <T>(
      path: string,
      params?: Record<string, string | number | boolean | undefined>,
      signal?: AbortSignal,
    ): Promise<T> => {
      const url = new URL(`${apiBaseUrl}${path}`);

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            url.searchParams.append(key, String(value));
          }
        });
      }

      try {
        const headers = await getHeaders();
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await response.json();
      } catch (error: unknown) {
        // Let AbortError bubble so React Query can treat it as a cancellation
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const apiPost = useCallback(
    async <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method: 'POST',
          headers,
          body: body ? JSON.stringify(body) : undefined,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await readBody<T>(response);
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const apiPut = useCallback(
    async <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method: 'PUT',
          headers,
          body: body ? JSON.stringify(body) : undefined,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await readBody<T>(response);
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const apiDelete = useCallback(
    async <T>(path: string, signal?: AbortSignal): Promise<T> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method: 'DELETE',
          headers,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await readBody<T>(response);
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const apiPutMultipart = useCallback(
    async <T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> => {
      try {
        const headers = await getHeaders();
        const { 'Content-Type': _ct, ...headersWithoutContentType } = headers as Record<string, string>;
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method: 'PUT',
          headers: headersWithoutContentType,
          body: formData,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await response.json();
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  const apiPostMultipart = useCallback(
    async <T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> => {
      try {
        const headers = await getHeaders();
        // Remove Content-Type so the browser sets the multipart boundary automatically
        const { 'Content-Type': _ct, ...headersWithoutContentType } = headers as Record<string, string>;
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method: 'POST',
          headers: headersWithoutContentType,
          body: formData,
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
        }

        return await response.json();
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          throw error;
        }
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
      }
    },
    [apiBaseUrl, getHeaders],
  );

  return {
    apiGet,
    apiPost,
    apiPut,
    apiPutMultipart,
    apiDelete,
    apiPostMultipart,
    apiBaseUrl,
    getHeaders,
    authenticatedFetch,
  };
}
